import React from 'react';
import { FiShuffle } from 'react-icons/fi';
import { AvatarColorEditor } from '../../components/Avatar';
import { useTranslation } from '../../contexts/TranslationContext';
import { COUNTRIES } from './registerConstants';
import { RegisterFormState } from './registerTypes';

interface RegisterStepIdentityProps {
  form: RegisterFormState;
  update: (patch: Partial<RegisterFormState>) => void;
  onContinue: () => void;
  onNotExpert: () => void;
}

const randomAvatarSeed = () => Math.floor(Math.random() * 900000) + 100000;

const RegisterStepIdentity: React.FC<RegisterStepIdentityProps> = ({
  form,
  update,
  onContinue,
  onNotExpert,
}) => {
  const { t } = useTranslation();

  const colorLabels = {
    color1: t.expertPanel.avatarColor1,
    color2: t.expertPanel.avatarColor2,
    pickColor: t.expertPanel.avatarPickColor,
  };

  const canContinue = form.fullName.trim().length > 0 && form.country.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canContinue) onContinue();
  };

  return (
    <div className="register__card">
      <h1 className="register__title italic-title">{t.register.identityTitle}</h1>
      <p className="register__subtitle">{t.register.identitySubtitle}</p>

      <form className="register__form" onSubmit={handleSubmit}>
        <div className="register__avatar-controls">
          <AvatarColorEditor
            color1={form.avatarColor1}
            color2={form.avatarColor2}
            labels={colorLabels}
            onColor1Change={(hex) => update({ avatarColor1: hex })}
            onColor2Change={(hex) => update({ avatarColor2: hex })}
          />
          <button
            type="button"
            className="register__shuffle"
            onClick={() => update({ avatarSeed: randomAvatarSeed() })}
            aria-label={t.register.shuffle}
          >
            <FiShuffle />
          </button>
        </div>

        <div className="register__field">
          <label htmlFor="fullName" className="register__label">{t.register.nameLabel}</label>
          <input
            id="fullName"
            className="register__input"
            type="text"
            maxLength={40}
            placeholder={t.register.namePlaceholder}
            value={form.fullName}
            onChange={(e) => update({ fullName: e.target.value })}
            required
          />
        </div>

        <div className="register__field">
          <label htmlFor="country" className="register__label">{t.register.countryLabel}</label>
          <select
            id="country"
            className="register__input register__select"
            value={form.country}
            onChange={(e) => update({ country: e.target.value })}
            required
          >
            <option value="">{t.register.selectCountry}</option>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>

        <button type="submit" className="register__submit button_primary" disabled={!canContinue}>
          {t.register.continue}
        </button>

        <button type="button" className="register__skip-link" onClick={onNotExpert}>
          {t.register.notExpertLink}
        </button>
      </form>
    </div>
  );
};

export default RegisterStepIdentity;
